import {
  analyseContractDocuments,
  deleteContract,
  getContractDetails,
  updateContract,
} from '@/app/services/contractsService';
import { Contract } from '@/app/types/models';
import { mutationKeys, queryKeys, SummarizationNamespaces } from '@/app/utils/constants';
import { toastManager } from '@/app/ui/components/toast';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { UpdateContractRequest } from '@/app/types/Dtos/request';
import { getModelDetailsQueryKey } from '@/app/components/features/evaluations/Summary/helpers';
import { useParams } from '@tanstack/react-router';

export const getContractDetailsQuery = (contractId: string) =>
  queryOptions({
    staleTime: 600 * 1000,
    queryKey: [queryKeys.CONTRACT_DETAILS, contractId],
    queryFn: async () => {
      const { data } = await getContractDetails(contractId);
      return data;
    },
    refetchIntervalInBackground: true,
  });

export const useAnalyseContractDocs = () => {
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationKey: [mutationKeys.ANALYSE_CONTRACT_DOCUMENTS],
    mutationFn: async (contractId: string) => {
      const res = await analyseContractDocuments(contractId);
      return { res, contractId };
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('errorAnalysisRedirecting'),
      });
    },
  });
};

export const useUpdateContract = () => {
  const { contractId } = useParams({
    from: '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout',
  });
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationFn: async (payload: UpdateContractRequest) => {
      return await updateContract(payload, contractId);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: getModelDetailsQueryKey(SummarizationNamespaces.CONTRACT, contractId),
      });
      toastManager.add({
        type: 'success',
        title: t('updatedSuccessfully'),
      });
    },
  });
};

export const useDeleteContract = (props: { contractId?: string; onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');

  return useMutation({
    mutationFn: async (contractId: string) => {
      return await deleteContract(contractId);
    },
    onSuccess: async (_, variables) => {
      const previousData = queryClient.getQueryData<Contract[]>([queryKeys.CONTRACTS]);
      queryClient.setQueryData(
        [queryKeys.CONTRACTS],
        previousData?.filter((c) => c.id !== variables),
      );
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.CONTRACTS],
      });
      props?.onSuccess?.();
      toastManager.add({
        type: 'success',
        title: t('deletedSuccessfully'),
      });
    },
  });
};
